import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Camera, LogOut, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import BookingCard from "@/components/dashboard/BookingCard";
import BookingHistory from "@/components/dashboard/BookingHistory";
import type { Booking } from "@/types";
import { BottomNav } from "@/components/navigation/BottomNav";
import { toast } from "sonner";
import { invokeFunctionWithAuth } from "@/lib/supabase/functions";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  // Função para recarregar bookings
  const loadBookings = useCallback(async () => {
    // Fetch via edge function with JWT
    if (user) {
      try {
        const { data, error } = await invokeFunctionWithAuth('get-customer-bookings', {
          phone: user.phone,
          userId: user.id // Pass userId when available
        });

        if (error) {
          throw error;
        }
        
        
        // Transform the data to match the Booking type
        const transformedBookings: Booking[] = (data.bookings || []).map((booking: any) => ({
          ...booking,
          filmmaker: booking.filmmaker ? {
            id: booking.filmmaker.id,
            bio: booking.filmmaker.bio,
            rating: booking.filmmaker.rating,
            full_name: booking.filmmaker.profiles?.full_name || 'Filmmaker',
            phone: booking.filmmaker.profiles?.phone || '',
            email: booking.filmmaker.profiles?.email || '',
            role: 'filmmaker' as const,
          } : undefined
        }));
        
        setBookings(transformedBookings);
      } catch (error: any) {
        toast.error('Failed to load bookings');
      }
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    loadBookings();
    
    // Use Supabase Realtime
    if (user?.id) {
      // Setting up Realtime subscription for customer bookings
      
      const channel = supabase
        .channel('customer-bookings')
        .on(
          'postgres_changes',
          {
            event: '*', // INSERT, UPDATE, DELETE
            schema: 'public',
            table: 'bookings',
            filter: `customer_id=eq.${user.id}`,
          },
          (payload) => {
            console.log('📊 Booking change detected in Dashboard (realtime):', payload.eventType);
            // Recarregar lista de bookings quando houver mudança
            loadBookings();
          }
        )
        .subscribe((status) => {
          console.log('🔴 Dashboard Realtime subscription status:', status);
          if (status === 'SUBSCRIBED') {
            console.log('✅ Successfully subscribed to customer bookings');
          } else if (status === 'CHANNEL_ERROR') {
            console.error('❌ Dashboard Realtime subscription error');
          }
        });

      return () => {
        console.log('🧹 Cleaning up Dashboard Realtime subscription');
        supabase.removeChannel(channel);
      };
    }
  }, [user, loadBookings]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const activeBookings = bookings.filter(b => 
    !['completed', 'canceled'].includes(b.status)
  );
  
  const historyBookings = bookings.filter(b => 
    ['completed', 'canceled'].includes(b.status)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading dashboard...</p>
      </div>
    );
  }

  const userDisplayName = user?.fullName || user?.phone || 'User';
  const userAvatarUrl = user?.avatar_url;

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-10 bg-white px-5 py-4 border-b border-[#E1E3E6]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-400 mb-1">Welcome!</p>
            <h1 className="text-2xl font-bold text-black">{userDisplayName}</h1>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="cursor-pointer hover:opacity-80 transition-opacity">
                <Avatar className="h-12 w-12 border-2 border-orange-500">
                  {userAvatarUrl && <AvatarImage src={userAvatarUrl} alt={userDisplayName} />}
                  <AvatarFallback className="bg-gray-100 text-gray-600">
                    {userDisplayName.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="px-4 pt-6 pb-24 space-y-6 min-h-[calc(100vh-80px)]" style={{ backgroundColor: '#F9FAFB' }}>
        {/* Active Bookings - Current Shooting */}
        <section>
          <h2 className="text-lg font-semibold text-left text-gray-800 mb-4">Your Current Shooting</h2>
          {activeBookings.length > 0 ? (
            <div className="space-y-5">
              {activeBookings.map((booking) => (
                <BookingCard key={booking.id} booking={booking} />
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center bg-white rounded-xl shadow-sm">
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center">
                  <Camera className="h-8 w-8 text-gray-400" />
                </div>
              </div>
              <h3 className="font-semibold text-lg mb-2 text-black">No active bookings</h3>
              <p className="text-sm text-gray-500 mb-4">
                Ready to book your next filming session?
              </p>
              <Link to="/book/step-1">
                <Button variant="outline">Book Now</Button>
              </Link>
            </Card>
          )}
        </section>

        {/* Booking History - Past Shootings */}
        {historyBookings.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Your Past Shootings</h2>
              <Link to="/dashboard/history" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
                See All
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <BookingHistory bookings={historyBookings} />
          </section>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}